import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, shareReplay, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Constante } from '../../constante';
import { EmpresaApi, HorarioApi, MetodoPagoApi, TallerApi } from '../models/api/empresa-api';
import { ResponseApi } from '../models/response-api';
import { AppUtil } from '../utility/app-util';

@Injectable({ providedIn: 'root' })
export class EmpresaService {
    private http = inject(HttpClient);
    private url = AppUtil.addTrailingSlash(environment.URL_API_PEDIDOS) + 'api/empresa/';
    private cache$?: Observable<EmpresaApi | null>;

    getEmpresa(): Observable<EmpresaApi | null> {
        if (!this.cache$) {
            this.cache$ = this.http.get<ResponseApi<EmpresaApi>>(this.url + 'get-empresa').pipe(
                map((res) => (res.success === Constante.REQUEST_STATUS_OK && res.data ? res.data : null)),
                shareReplay(1)
            );
        }
        return this.cache$;
    }

    invalidate(): void {
        this.cache$ = undefined;
    }

    updateEmpresa(body: Partial<EmpresaApi>): Observable<boolean> {
        return this.http.put<ResponseApi<unknown>>(this.url + 'update-empresa', body).pipe(
            tap(() => this.invalidate()),
            map((res) => res.success === Constante.REQUEST_STATUS_OK)
        );
    }

    getTaller(): Observable<TallerApi | null> {
        return this.http.get<ResponseApi<TallerApi>>(this.url + 'get-taller').pipe(
            map((res) => (res.success === Constante.REQUEST_STATUS_OK && res.data ? res.data : null))
        );
    }

    updateTaller(body: {
        nombre: string;
        direccion: string;
        distrito?: string | null;
        latitud?: number | null;
        longitud?: number | null;
        urlMaps?: string | null;
        telefono?: string | null;
    }): Observable<boolean> {
        return this.http.put<ResponseApi<unknown>>(this.url + 'update-taller', body).pipe(
            map((res) => res.success === Constante.REQUEST_STATUS_OK)
        );
    }

    listHorarios(): Observable<HorarioApi[]> {
        return this.http.get<ResponseApi<HorarioApi[]>>(this.url + 'list-horarios').pipe(
            map((res) => (res.success === Constante.REQUEST_STATUS_OK && Array.isArray(res.data) ? res.data : []))
        );
    }

    updateHorario(body: {
        uuid?: string | null;
        diaSemana: number;
        horaInicio?: string | null;
        horaFin?: string | null;
        esCerrado: boolean;
    }): Observable<boolean> {
        return this.http.put<ResponseApi<unknown>>(this.url + 'update-horario', body).pipe(
            map((res) => res.success === Constante.REQUEST_STATUS_OK)
        );
    }

    listMetodosPago(): Observable<MetodoPagoApi[]> {
        return this.http.get<ResponseApi<MetodoPagoApi[]>>(this.url + 'list-metodos-pago').pipe(
            map((res) => (res.success === Constante.REQUEST_STATUS_OK && Array.isArray(res.data) ? res.data : []))
        );
    }

    createMetodoPago(body: {
        tipoMetodoPagoId: number;
        numero: string;
        titular: string;
        instrucciones?: string | null;
        imagenUuid?: string | null;
        orden: number;
    }): Observable<boolean> {
        return this.http.post<ResponseApi<unknown>>(this.url + 'create-metodo-pago', body).pipe(
            map((res) => res.success === Constante.REQUEST_STATUS_OK)
        );
    }

    updateMetodoPago(uuid: string, body: {
        tipoMetodoPagoId: number;
        numero: string;
        titular: string;
        instrucciones?: string | null;
        imagenUuid?: string | null;
        orden: number;
    }): Observable<boolean> {
        return this.http.put<ResponseApi<unknown>>(this.url + 'update-metodo-pago/' + uuid, body).pipe(
            map((res) => res.success === Constante.REQUEST_STATUS_OK)
        );
    }
}
